import React from 'react';
import { LinkDD, LinkSimple, getBaseUrl } from '../Stories/Links';
import { replaceLegCodes } from './Story';
import styled from 'styled-components';

const NoteWrapper = styled.div`
  max-width: 800px;
`;

const Note = styled.ul`
  margin: 0;
  padding: 0;

  & li {
    list-style-type: disc;
    margin-inline-start: 1rem;
    margin-block-end: 0.5rem;
  }

  & a {
    color: #0a819f;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
`;

const NoteTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-block: 0 1rem;
`;

function renderBullet(tip, personas, score=0) {
  let textContent = '';
  if (tip?.data?.length > 1 && tip.data !== 'nan') {
    textContent = replaceLegCodes(tip.data, personas);
  }
  if (tip?.link_url?.length > 5) {
    if (tip?.link_text?.length > 1) {
      textContent = (
        <>
          {textContent}&nbsp;(
          {LinkSimple(tip.link_text, tip.link_url, null, false)})
        </>
      );
    } else {
      textContent = (
        <>
          {textContent}&nbsp;({LinkSimple('link', tip.link_url, null, false)})
        </>
      );
    }
  } else {
    textContent = tip?.data || '';
  }
  if ((score > 0) && textContent.length > 0)
    textContent = '[+'+score.toString()+'] ' + textContent;
  return textContent;
}

export const NotesMethodology = ({ tipsheet, bill }) => {
  const content = tipsheet?.content;
  if (!content?.length) return null;

  // Avoid mutating the original array on every render
  let localNullPhenoms = [
    'endofintro',
    'intro',
    'bill_digest',
    'bill_ai_summary',
    'governor_veto_message',
  ];

  let firstNote = {
    title: 'Story Idea',
    description:
      'The idea and summary block is produced by Generative AI with specific inputs and a custom prompt.',
    bullets: content
      .filter(
        (item) =>
          item.is_story_phenom && !localNullPhenoms.includes(item.phenom)
      )
      .map((item) => {
        let allBullets = [];
        localNullPhenoms.push(item.phenom);
        item.tips?.forEach((tip) => {
          if (tip.type === 'bullet') {
            allBullets.push(renderBullet(tip, tipsheet.personas,item.score));
          }
        });
        return allBullets;
      }),
  };

  if (firstNote.bullets?.length > 0) {
    firstNote.description += ' The following are the inputs supplied:';
  }
  if (tipsheet?.bill_name?.toLowerCase() === 'no bill discussed') {
    firstNote.description = (
      <>
        The idea and summary block is produced by Generative AI based on the{' '}
        {LinkDD(
          'transcript',
          bill?.hearing_id,
          'hea',
          null,
          false,
          null,
          '#t=' + bill?.offset + '&f=' + bill?.file_id
        )}{' '}
        of the hearing.
      </>
    );
  }

  let secondNote = {
    title: 'Bill Summary',
    description:
      'Bill summary is produced by Generative AI using facts about the bill and a custom prompt.',
    end_note: (
      <>
        See{' '}
        {LinkDD(
          'bill information',
          tipsheet?.bill_id,
          'bil',
          tipsheet?.bill_id,
          false
        )}{' '}
        from Digital Democracy.
      </>
    ),
    bullets: [`Bill name: ${tipsheet?.bill_name}`],
  };

  if (content.some((item) => item.phenom === 'bill_analysis_summary')) {
    secondNote.bullets.push(
      'The committee analysis produced by committee staff.'
    );
  }
  secondNote.bullets.push(
    'Bill digest (called "bill summary" on Digital Democracy) produced by the legislature.'
  );

  let newNote = {
    title: 'General Content',
    description:
      'General content for Digital Democracy Tip Sheets, including what is summarized and paraphrased ' +
      'with the help of Large Language Models, is derived through a series of AI modules designed to find ' +
      'basic facts or newsworthy observations about this discussion. ',
    bullets: [],
    end_note: (
      <>
        See also Digital Democracy{' '}
        {LinkSimple(
          'Data Sources and Methodology page',
          getBaseUrl() + '/data-sources-methodology',
          null,
          false
        )}
      </>
    ),
  };

  content
    .filter((item) => !localNullPhenoms.includes(item.phenom))
    .forEach((item) => {
      if (item.tips?.length) {
        item.tips.forEach((tip) => {
          newNote.bullets.push(renderBullet(tip, tipsheet.personas,item.score?item.score:0));
        });
        localNullPhenoms.push(item.phenom);
      }
    });

  let notes =
    tipsheet?.bill_name?.toLowerCase() === 'no bill discussed'
      ? [firstNote, newNote]
      : [firstNote, secondNote, newNote];

  return (
    <NoteWrapper>
      {notes.map((note) => (
        <Note key={note.title}>
          <NoteTitle>{note.title}</NoteTitle>
          <p>{note.description}</p>
          {note.bullets.map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
          <p>{note.end_note}</p>
        </Note>
      ))}
    </NoteWrapper>
  );
};
